import React from "react";
import { Link, Navigate } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import DashboardOwner from "@/components/DashboardOwner";
import PriceListPanel from "@/components/PriceListPanel";
import { useAuth } from "@/context/AuthContext";
import { labelPeran } from "@/lib/roles";
import { menuForRole, homeForRole } from "@/lib/menu";
import { ShieldCheck } from "lucide-react";

function RoleLanding({ user }) {
  // G1-A: shortcuts follow the role's menu (first items of each group).
  const links = menuForRole(user.role).flatMap((g) => g.items.filter((it) => !it.external).slice(0, 2))
    .map((it) => [it.to, it.label]);
  return (
    <div className="space-y-6">
      <section
        data-testid="welcome-banner"
        className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-5 py-4 flex items-center gap-3"
      >
        <ShieldCheck size={20} className="text-emerald-400 shrink-0" />
        <p className="text-sm text-emerald-100">
          Login berhasil sebagai <strong data-testid="welcome-username">{user?.username}</strong> — role{" "}
          <span data-testid="welcome-role" className="font-mono text-emerald-300">{labelPeran(user)}</span>.
        </p>
      </section>
      <div className="flex flex-wrap gap-3">
        {links.map(([to, label]) => (
          <Link
            key={to}
            to={to}
            data-testid={`landing-link-${label.toLowerCase().replace(/\s/g, "-")}`}
            className="rounded-lg border border-slate-700 bg-slate-800/60 px-5 py-3 text-sm text-slate-200 hover:border-amber-500/40 hover:text-amber-300"
          >
            Buka {label}
          </Link>
        ))}
      </div>
      {/* owner 2026-10-11: Kasir/CS see the Price List on their home page (added, nothing replaced) */}
      {user.role === "kasir" && (
        <section data-testid="home-price-list" className="space-y-3">
          <h2 className="text-xl font-bold tracking-tight text-slate-100">Price List</h2>
          <PriceListPanel testId="home-price-list-panel" />
        </section>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const home = homeForRole(user?.role);
  if (home !== "/") return <Navigate to={home} replace />;

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      {/* owner 2026-10-11/12: the owner lands on the dashboard (mockup v2); other roles keep their home page */}
      <main className={`${isAdmin ? "max-w-[1280px] py-5 sm:py-8" : "max-w-6xl py-8 sm:py-12"} mx-auto px-4 sm:px-6 lg:px-8`}>
        {isAdmin ? <DashboardOwner /> : <RoleLanding user={user} />}
      </main>
      <footer className="border-t border-slate-800 py-6 text-center text-xs font-mono text-slate-500">
        SOQPRINT · Sistem Produksi &amp; Tracking SPK
      </footer>
    </div>
  );
}
